import { newElementWith } from "@excalidraw/element";
import { CaptureUpdateAction } from "@excalidraw/excalidraw";

import { isMindmapNode } from "./drawolaMindmapModel";
import { MINDMAP_DATA_KEY } from "./types";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

const getDataFromAnyElement = (
  el: ExcalidrawElement,
): { mindmapId: string; nodeId: string; parentId: string | null } | null => {
  const data = (el.customData as Record<string, unknown> | undefined)?.[
    MINDMAP_DATA_KEY
  ] as
    | { mindmapId: string; nodeId: string; parentId: string | null }
    | undefined;
  return data ?? null;
};

// On any mindmap-node deletion, mark its descendants and any arrows that
// connect to the deleted set (including the original node's incoming arrow
// from its parent). This lets a regular Excalidraw delete on a mindmap
// rectangle leave the rest of the tree consistent: no orphan children, no
// floating arrows pointing at a deleted endpoint.
//
// `newlyDeletedElementIds` is the set of element ids that just transitioned
// from alive to deleted (detected by comparing successive onChange snapshots).
export const cascadeDeleteMindmapSubtree = (
  api: ExcalidrawImperativeAPI,
  newlyDeletedElementIds: readonly string[],
) => {
  const elements = api.getSceneElementsIncludingDeleted();
  const elementById = new Map(elements.map((el) => [el.id, el]));

  // Resolve the mindmap nodeIds and element ids of the newly-deleted
  // elements (only those that actually carried mindmap customData).
  const deletedMindmapNodeIds = new Set<string>();
  // Element ids of every rectangle we want to be considered "gone" for the
  // purposes of arrow / bound-text cleanup. Starts with the originally
  // deleted rectangles and grows with their descendants.
  const deletedRectIds = new Set<string>();
  for (const elementId of newlyDeletedElementIds) {
    const el = elementById.get(elementId);
    if (!el) {
      continue;
    }
    const data = getDataFromAnyElement(el);
    if (data) {
      deletedMindmapNodeIds.add(data.nodeId);
      deletedRectIds.add(elementId);
    }
  }
  if (deletedRectIds.size === 0) {
    return;
  }

  // BFS over alive mindmap nodes whose parent chain leads to a deleted one.
  const queue = [...deletedMindmapNodeIds];
  while (queue.length > 0) {
    const parentNodeId = queue.shift()!;
    for (const el of elements) {
      if (!isMindmapNode(el)) {
        continue;
      }
      const data = el.customData[MINDMAP_DATA_KEY];
      if (data.parentId === parentNodeId && !deletedRectIds.has(el.id)) {
        deletedRectIds.add(el.id);
        queue.push(data.nodeId);
      }
    }
  }

  // Excalidraw's fixBindingsAfterDeletion (binding.ts BindableElement.
  // unbindAffected) nulls the startBinding/endBinding of any arrow that
  // pointed at a now-deleted rectangle by the time our onChange handler
  // runs. The arrow itself is left alive with a dangling endpoint. We use
  // two complementary detections:
  //
  // 1) Any arrow id referenced in a deleted rectangle's boundElements (the
  //    deleted rect is the SOURCE of truth — Excalidraw does not touch
  //    boundElements during fixBindingsAfterDeletion).
  // 2) Any arrow whose startBinding OR endBinding is now null. We always
  //    create mindmap arrows with both bindings, so a null binding means
  //    Excalidraw stripped it on deletion of the other endpoint.
  const arrowsToCleanup = new Set<string>();

  // Build an index from mindmap nodeId -> rectangle element so we can walk
  // the topology even when Excalidraw has already nulled the arrow
  // bindings. customData carries nodeId regardless of isDeleted, so this
  // also reaches the just-deleted rectangle that started the cascade.
  const rectByMindmapNodeId = new Map<string, ExcalidrawElement>();
  for (const el of elements) {
    const data = getDataFromAnyElement(el);
    if (data) {
      rectByMindmapNodeId.set(data.nodeId, el);
    }
  }

  // Layer 1 (parent-edge cleanup): for every deleted rectangle, find its
  // parent block via customData and mark the connecting arrow for delete.
  // The arrow is the one whose id appears in BOTH the deleted rect's and
  // its parent's boundElements (which Excalidraw does not touch on
  // deletion). This catches the failure case the user reported: when a
  // child block is removed, the parent->child arrow has its endBinding
  // nulled but the boundElements references on either side remain.
  for (const rectId of deletedRectIds) {
    const rect = elementById.get(rectId);
    const data = rect ? getDataFromAnyElement(rect) : null;
    if (!rect || !data || data.parentId === null) {
      continue;
    }
    const parent = rectByMindmapNodeId.get(data.parentId);
    if (!parent?.boundElements || !rect.boundElements) {
      continue;
    }
    const childArrows = new Set(
      rect.boundElements
        .filter((ref) => ref.type === "arrow")
        .map((ref) => ref.id),
    );
    for (const ref of parent.boundElements) {
      if (ref.type === "arrow" && childArrows.has(ref.id)) {
        arrowsToCleanup.add(ref.id);
      }
    }
  }

  // Layer 2 (subtree-edge cleanup): every arrow id referenced from any
  // deleted rectangle's boundElements is a parent->child edge inside the
  // dragged subtree, so it must go too.
  for (const rectId of deletedRectIds) {
    const el = elementById.get(rectId);
    if (!el?.boundElements) {
      continue;
    }
    for (const ref of el.boundElements) {
      if (ref.type === "arrow") {
        arrowsToCleanup.add(ref.id);
      }
    }
  }

  // Layer 3 (orphan sweep): any arrow referenced by a mindmap node whose
  // start or end binding is now null is necessarily orphaned — we always
  // create mindmap arrows with both bindings set, so a null one means
  // Excalidraw stripped it after the partner rectangle was deleted.
  // Restricting to mindmap-referenced arrows avoids touching unrelated
  // user-drawn arrows that may legitimately have a null endpoint.
  const mindmapArrowIds = new Set<string>();
  for (const el of elements) {
    if (!getDataFromAnyElement(el) || !el.boundElements) {
      continue;
    }
    for (const ref of el.boundElements) {
      if (ref.type === "arrow") {
        mindmapArrowIds.add(ref.id);
      }
    }
  }
  for (const el of elements) {
    if (el.type !== "arrow" || el.isDeleted) {
      continue;
    }
    if (!mindmapArrowIds.has(el.id)) {
      continue;
    }
    if (!el.startBinding || !el.endBinding) {
      arrowsToCleanup.add(el.id);
    }
  }

  // Collect everything to delete: descendant rectangles, their bound text,
  // and the cleanup arrows.
  const finalDelete = new Set<string>();
  for (const el of elements) {
    if (el.isDeleted) {
      continue;
    }
    if (
      el.type === "text" &&
      el.containerId &&
      deletedRectIds.has(el.containerId)
    ) {
      finalDelete.add(el.id);
      continue;
    }
    if (el.type === "arrow") {
      const startId = el.startBinding?.elementId;
      const endId = el.endBinding?.elementId;
      if (
        (startId && deletedRectIds.has(startId)) ||
        (endId && deletedRectIds.has(endId)) ||
        arrowsToCleanup.has(el.id)
      ) {
        finalDelete.add(el.id);
      }
      continue;
    }
    if (deletedRectIds.has(el.id)) {
      finalDelete.add(el.id);
    }
  }

  if (finalDelete.size === 0) {
    return;
  }

  const updated = elements.map((el) =>
    finalDelete.has(el.id) ? newElementWith(el, { isDeleted: true }) : el,
  );

  api.updateScene({
    elements: updated,
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });
};

// Build a snapshot of mindmap-element ids that are currently flagged
// isDeleted. Used by the overlay to diff against the previous tick and
// detect newly-deleted nodes.
export const collectDeletedMindmapElementIds = (
  elements: readonly ExcalidrawElement[],
): Set<string> => {
  const out = new Set<string>();
  for (const el of elements) {
    if (el.isDeleted && getDataFromAnyElement(el)) {
      out.add(el.id);
    }
  }
  return out;
};
